
import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';

import SubPagesLayout from '../../../UI/layout/subPages';
import InstructorAssignmentForm from '../../../UI/instructor/settings/assignments/form';

import { assignmentTypeMappings } from '../../../../vars';
import { formatScnearioLevelToFilterName, shuffleArray, getTargetEntityType, matchesEntityType } from '../../../../utils';
import { buildRealcoLevelName } from '../../../Classification/realcoData';

import { 
  navigation,
  gameCreateItemsQuestionsColumns,
  gameCreateItemsOptionsNormal,
  scenarioLevelsDescription,
  designTypesMap
} from './data';

import {
  generalInfoQuery,
  instructorScenarioLevelsQuery,
  coursesSetupQuery,
  editScenario,
  editGame,
  createScenarioLevels,
  editScenarioLevel,
  createItemQuestionsBulk,
  deleteItemQuestions,
  createItemOptionsBulk,
  deleteItemOptions,
  deleteScenarioLevel,
  scenarioAddCourse,
} from './api';

import InstructorAssignmentSetLevels from '../../../UI/instructor/settings/assignments/setLevels';

export default function InstructorSettingsEditAssignment() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);

  const { data: generalInfo } = useQuery(generalInfoQuery());

  const sessions = generalInfo?.sessions;
  const games = generalInfo?.games;

  const params = useParams();
  const assignmentId = Number(params.assignmentId);
  const [formData, setFormData] = useState({});
  const [scenarioId, setScenarioId] = useState(0);

  // One source-template scenario per game type — replaces the prior
  // multi-source lookups (entity-type / special-accounts for class+
  // normal, be-corporate / be-noncorporate for equation, principles-
  // corporate / principles-noncorporate / principles-assumptions-
  // constraints for principles). The "Add a Level" modal pulls
  // templates from these single scenarios; the level-creation flow
  // copies questions/options off the matching level inside.
  const [classificationGameScenarioId, setClassificationGameScenarioId] = useState(null);
  const [normalGameScenarioId, setNormalGameScenarioId] = useState(null);
  const [beGameScenarioId, setBeGameScenarioId] = useState(null);
  const [principlesGameScenarioId, setPrinciplesGameScenarioId] = useState(null);

  const [editingScenarioLevelIndex, setEditingScenarioLevelIndex] = useState(null);
  const [editingScenarioLevelPrecheckedItems, setEditingScenarioLevelPrecheckedItems] = useState([]);
  const [editingScenarioLevelPrecheckedDesigns, setEditingScenarioLevelPrecheckedDesigns] = useState([]);

  const [isGameStarted, setIsGameStarted] = useState(false);
  
  const { data: scenarioLevels, refetch: refetchScenarioLevels } = useQuery(instructorScenarioLevelsQuery({scenarioId}));

  const { data: classificationGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: classificationGameScenarioId }));
  const { data: normalGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: normalGameScenarioId }));
  const { data: beGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: beGameScenarioId }));
  const { data: principlesGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: principlesGameScenarioId }));

  const { data: scenarios } = useQuery(coursesSetupQuery());
  
  const courses = generalInfo?.courses;
  
  const editScenarioMutation = useMutation({
    mutationFn: (data) => editScenario(data)
  });
  
  const editGameMutation = useMutation({
    mutationFn: (data) => editGame(data)
  });

  const createScenarioLevelsMutation = useMutation({
    mutationFn: (data) => createScenarioLevels(data)
  });

  const editScenarioLevelsMutation = useMutation({
    mutationFn: (data) => editScenarioLevel(data)
  });
  
  const createItemQuestionsBulkMutation = useMutation({
    mutationFn: (data) => createItemQuestionsBulk(data)
  });
  
  const deleteItemQuestionsMutation = useMutation({
    mutationFn: (data) => deleteItemQuestions(data)
  });
  
  const createItemOptionsBulkMutation = useMutation({
    mutationFn: (data) => createItemOptionsBulk(data)
  });
  
  const deleteItemOptionsMutation = useMutation({
    mutationFn: (data) => deleteItemOptions(data)
  });

  const deleteScenarioLevelMutation = useMutation({
    mutationFn: (data) => deleteScenarioLevel(data)
  });

  const scenarioAddCourseMutation = useMutation({
    mutationFn: (data) => scenarioAddCourse(data)
  });

  // Guards against the probe-and-link useEffect re-attempting the
  // same scenario across the inevitable re-renders triggered by the
  // useQuery invalidation that follows scenarioAddCourse.
  const attemptedLinkScenarioIdsRef = useRef(new Set());


  useEffect(() => {
    if (sessions && assignmentId && generalInfo?.users) {
      const currentUser = generalInfo.users;
      const isCurrentGameSession = sessions.some(
        session => session.user !== currentUser.id && session.game === assignmentId
      );
      setIsGameStarted(isCurrentGameSession);
    }
  }, [sessions, assignmentId, generalInfo?.users]);

  useEffect(() => {
    if (games && assignmentId) {
      const currentGame = games.find((game) => game.id === assignmentId);
      if (currentGame) {
        setFormData({
          name: currentGame.name || '',
          description: currentGame.description || '',
          startTime: currentGame.start_time || '',
          endTime: currentGame.end_time || '',
          assignmentType: currentGame.game_type
        });

        setScenarioId(currentGame.scenario);
      }
    }
  }, [games, assignmentId]);

  const handleEditGameSettings = async ({
    name,
    description,
    startTime,
    endTime,
    formSavedCallback
  }) => {
    setIsLoading(true);

    if(formData.name !== name) {
      await editScenarioMutation.mutateAsync({
        id: scenarioId,
        name
      }).catch((error) => {
        console.error('Caught error with mutateAsync:', error);
      });
    }

    await editGameMutation.mutateAsync({
      id: assignmentId,
      name,
      description,
      startTime,
      endTime
    }).catch((error) => {
      console.error('Caught error with mutateAsync:', error);
    });

    queryClient.invalidateQueries({ queryKey: ['generalInfo'] });

    if(formSavedCallback) {
      formSavedCallback();
    }
    setIsLoading(false);
  }

  const handleShowLevelAccountsButtonClick = (scenarioLevelIndex) => {
    setEditingScenarioLevelIndex(scenarioLevelIndex);
    const edititngSceenarioLevel = scenarioLevels[scenarioLevelIndex];

    const savedAccountItems = assignmentTypeShortened?.includes('classification')
      ? edititngSceenarioLevel?.questions.map(({ id, account_item }) => ({id, text: account_item.name}))
      : assignmentTypeShortened?.includes('normal')
      ? edititngSceenarioLevel?.normal_questions.map(({ id, account_item }) => ({id, text: account_item.name}))
      : assignmentTypeShortened?.includes('equation')
      ? edititngSceenarioLevel?.be_questions.map(({ id, description }) => ({id, text: description}))
      : assignmentTypeShortened?.includes('principles')
      ? edititngSceenarioLevel?.transactions.map(({ id, description }) => ({id, text: description}))
      : [];

    setEditingScenarioLevelPrecheckedDesigns(edititngSceenarioLevel?.design);
    if(savedAccountItems) {
      setEditingScenarioLevelPrecheckedItems(savedAccountItems);
    } else {
      setEditingScenarioLevelPrecheckedItems([]);
    }

  }

  const assignmentTypeShortened = useMemo(() => {
    return (
      Object.entries(assignmentTypeMappings).find(([key]) => formData.assignmentType?.includes(key))?.[1] || ''
    );
  }, [formData.assignmentType]);

  // Resolve a single source-template scenario per game type from the
  // course-setup payload. Replaces the previous multi-source pattern
  // (entity-type + special-accounts, be-corporate + be-noncorporate,
  // principles-corporate + principles-noncorporate + principles-
  // assumptions-constraints).
  useEffect(() => {
    if (!scenarios) return;
    if (assignmentTypeShortened === 'classification') {
      setClassificationGameScenarioId(scenarios.find(s => s.name === 'classification-game')?.id || null);
    } else if (assignmentTypeShortened === 'normal') {
      setNormalGameScenarioId(scenarios.find(s => s.name === 'normal-game')?.id || null);
    } else if (assignmentTypeShortened === 'equation') {
      setBeGameScenarioId(scenarios.find(s => s.name === 'be-game')?.id || null);
    } else if (assignmentTypeShortened === 'principles') {
      setPrinciplesGameScenarioId(scenarios.find(s => s.name === 'principles-game')?.id || null);
    }
  }, [scenarios, assignmentTypeShortened]);

  // Ensure the source-template scenario for this assignment's game type
  // is linked to the instructor's course. Reuses the relevant
  // useQuery data above (one of classification/normal/be/principles
  // GameScenarioLevels) so we don't fire a duplicate GET — when the
  // useQuery returns an empty array the scenario isn't in this
  // instructor's course scope, and we call scenarioAddCourse + invalidate
  // to refetch.
  useEffect(() => {
    if (!scenarios || !assignmentTypeShortened || !courses?.length) return;

    const templateScenarioName =
      assignmentTypeShortened === 'classification' ? 'classification-game' :
      assignmentTypeShortened === 'normal' ? 'normal-game' :
      assignmentTypeShortened === 'equation' ? 'be-game' :
      assignmentTypeShortened === 'principles' ? 'principles-game' :
      null;
    if (!templateScenarioName) return;

    const templateScenario = scenarios.find(s => s.name === templateScenarioName);
    if (!templateScenario) return;

    const templateLevels =
      assignmentTypeShortened === 'classification' ? classificationGameScenarioLevels :
      assignmentTypeShortened === 'normal' ? normalGameScenarioLevels :
      assignmentTypeShortened === 'equation' ? beGameScenarioLevels :
      assignmentTypeShortened === 'principles' ? principlesGameScenarioLevels :
      undefined;

    // Wait for the relevant useQuery to settle (undefined = still
    // loading). Skip if the scenario already has linked levels.
    if (templateLevels === undefined) return;
    if (templateLevels.length > 0) return;
    // Don't retry — the useQuery invalidation below would otherwise
    // re-fire this effect and we'd call scenarioAddCourse on every
    // empty refetch.
    if (attemptedLinkScenarioIdsRef.current.has(templateScenario.id)) return;
    attemptedLinkScenarioIdsRef.current.add(templateScenario.id);

    let cancelled = false;
    (async () => {
      try {
        await scenarioAddCourseMutation.mutateAsync({
          scenarioId: templateScenario.id,
          courseId: courses[0].id,
        });
        if (cancelled) return;
        queryClient.invalidateQueries({ queryKey: ['instructorScenarioLevels', templateScenario.id] });
      } catch {
        // Benign — leaves modal in current state if linking fails.
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scenarios,
    assignmentTypeShortened,
    courses,
    classificationGameScenarioLevels,
    normalGameScenarioLevels,
    beGameScenarioLevels,
    principlesGameScenarioLevels,
  ]);

  // Map raw account-classification questions to the modal's row shape.
  // `normal` ('debit' / 'credit') passes through so the Normal Balance
  // modal can bucket items into Debit/Credit columns; items missing
  // that field are skipped at render time.
  //
  // Rows that don't belong to any of the 5 element columns (Assets /
  // Liabilities / Equity / Revenues / Expenses) are dropped by
  // returning null. The 5 Element account_items themselves live in
  // the source template's `questions` array (Level 1 needs them to
  // fall) but their own `parents` don't include any element name, so
  // without this filter they would render invisibly in the Level 2+
  // modal, get silently pre-selected by the default "select all"
  // behavior, and then fall as questions on the student's Level 2+
  // board. Level 1 is auto-created on a different path (add.js) that
  // picks the 5 Elements explicitly by name, so this filter has no
  // effect there.
  const mapAccountQuestionToFilterItem = ({ id, order, account_item, explanation }) => {
    const relatedItemQuestionColumn = gameCreateItemsQuestionsColumns.find(column =>
      account_item.parents?.includes(column)
    );
    if (!relatedItemQuestionColumn) return null;
    return {
      id,
      order,
      text: account_item.name,
      column: relatedItemQuestionColumn,
      accountItemId: account_item.id,
      explanation,
      normal: account_item.normal,
    };
  };

  const mapBeQuestionToFilterItem = ({ transaction_id, order, description }) => ({
    id: transaction_id,
    order,
    text: description,
    column: 'Transactions',
  });

  const mapPrinciplesTransactionToFilterItem = ({ id, order, description, transaction_id }) => ({
    id,
    order,
    text: description,
    column: 'Transactions',
    accountItemId: transaction_id,
  });

  // Per-game-type filter map. Top-level keys are decision-type labels
  // (matching `decisionTypes` in UI data); within each, sub-buckets are
  // template scenario levels matched to that decision type per the
  // single-source rules:
  //   classification (source: classification-game)
  //     - "Accounts"               → level named "classification-normal-template"
  //     - "Deferral & Contra…"     → level named "deferral-contra-template"
  //     - "Classified & Multi-Step"→ all design.includes('4')
  //     - "Real Companies"         → all design.includes('5')
  //   normal (source: normal-game)
  //     - "Accounts"           → level named "classification-normal-template"
  //     - "Deferral & Contra…" → level named "deferral-contra-template"
  //   equation (source: be-game)
  //     - any decision type → every level in be-game
  //   principles (source: principles-game)
  //     - "Revenue & Expense Recognition"        → all design.includes('2')
  //     - "Assumptions, Principles, & Constraints"→ all design.includes('4')
  // Decision types that have no template (class-elements / norm-
  // elements) intentionally aren't included — those levels are auto-
  // created elsewhere and the instructor doesn't pick templates for
  // them.
  // Course business-type filter — narrows source-template content to
  // the instructor's selected entity (corporate / noncorporate). For
  // BE / Principles the entity_type lives on the scenario level; for
  // Classification / Normal it lives on each question's account_item.
  // matchesEntityType is permissive when the target or the data is
  // missing (see utils.js).
  const targetEntityType = getTargetEntityType(courses?.[0]?.businesstype);
  const itemEntityFilter = (q) =>
    matchesEntityType(q?.account_item?.entity_type, targetEntityType);

  const filterItems = useMemo(() => {
    const filterLevelsByEntity = (levels) =>
      (levels || []).filter(l => matchesEntityType(l.entity_type, targetEntityType));

    // .filter(Boolean) on the mapped output drops any nulls the mapper
    // returned — mapAccountQuestionToFilterItem returns null for rows
    // that don't belong to any of the 5 element columns (the Element
    // account_items themselves). Other mappers (BE / Principles) never
    // return null, so the filter is a no-op for them.
    const buildLevelsBucket = (levels, mapItem, itemsKey) =>
      (levels || []).reduce((acc, level) => {
        const items = (level[itemsKey] || []).map(mapItem).filter(Boolean);
        acc[formatScnearioLevelToFilterName(level.name)] = items;
        return acc;
      }, {});

    const buildSingleNamedBucket = (levels, levelName, label, mapItem, itemsKey, perItemFilter) => {
      const matched = (levels || []).find(l => l.name === levelName);
      const raw = matched ? (matched[itemsKey] || []) : [];
      const filtered = perItemFilter ? raw.filter(perItemFilter) : raw;
      return { [label]: filtered.map(mapItem).filter(Boolean) };
    };

    const buildDesignFilteredBucket = (levels, designValue, mapItem, itemsKey, perItemFilter) =>
      (levels || [])
        .filter(l => Array.isArray(l.design) && l.design.includes(designValue))
        .reduce((acc, level) => {
          const raw = level[itemsKey] || [];
          const filtered = perItemFilter ? raw.filter(perItemFilter) : raw;
          acc[formatScnearioLevelToFilterName(level.name)] = filtered.map(mapItem).filter(Boolean);
          return acc;
        }, {});

    if (assignmentTypeShortened === 'classification') {
      const lvl = classificationGameScenarioLevels;
      if (!lvl?.length) return {};
      // "realco" levels belong exclusively to the "Real Companies"
      // decision type — hide them from every other bucket, and only
      // show them when the instructor selects "Real Companies".
      const nonRealCoLevels = lvl.filter(l => !l.name.toLowerCase().includes('realco'));
      const realCoLevels = lvl.filter(l => l.name.toLowerCase().includes('realco'));
      return {
        'Accounts': buildSingleNamedBucket(nonRealCoLevels, 'classification-normal-template', 'Accounts', mapAccountQuestionToFilterItem, 'questions', itemEntityFilter),
        'Deferral & Contra Account': buildSingleNamedBucket(nonRealCoLevels, 'deferral-contra-template', 'Deferral & Contra Account', mapAccountQuestionToFilterItem, 'questions', itemEntityFilter),
        'Classified & Multi-Step': buildDesignFilteredBucket(nonRealCoLevels, '4', mapAccountQuestionToFilterItem, 'questions', itemEntityFilter),
        'Real Companies': buildDesignFilteredBucket(realCoLevels, '5', mapAccountQuestionToFilterItem, 'questions', itemEntityFilter),
      };
    }

    if (assignmentTypeShortened === 'normal') {
      const lvl = normalGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Accounts': buildSingleNamedBucket(lvl, 'classification-normal-template', 'Accounts', mapAccountQuestionToFilterItem, 'normal_questions', itemEntityFilter),
        'Deferral & Contra Account': buildSingleNamedBucket(lvl, 'deferral-contra-template', 'Deferral & Contra Account', mapAccountQuestionToFilterItem, 'normal_questions', itemEntityFilter),
      };
    }

    if (assignmentTypeShortened === 'equation') {
      const lvl = filterLevelsByEntity(beGameScenarioLevels);
      if (!lvl.length) return {};
      // Levels whose name contains "income statement impact" are
      // scoped to the Income Statement Impact decision type only —
      // every other decision type hides them from its scenario list.
      const isIncomeStatementImpactLevel = (l) =>
        l?.name?.toLowerCase?.()?.includes?.('income statement impact') ?? false;
      const generalLevels = lvl.filter(l => !isIncomeStatementImpactLevel(l));
      const incomeStatementImpactLevels = lvl.filter(isIncomeStatementImpactLevel);
      const generalBucket = buildLevelsBucket(generalLevels, mapBeQuestionToFilterItem, 'be_questions');
      const incomeStatementImpactBucket = buildLevelsBucket(incomeStatementImpactLevels, mapBeQuestionToFilterItem, 'be_questions');
      return {
        'Increase or Decrease': generalBucket,
        'Debits & Credits': generalBucket,
        'Accounts Impacted': generalBucket,
        'Income Statement Impact': incomeStatementImpactBucket,
        'All Types': generalBucket,
      };
    }

    if (assignmentTypeShortened === 'principles') {
      const lvl = filterLevelsByEntity(principlesGameScenarioLevels);
      if (!lvl.length) return {};
      return {
        'Revenue & Expense Recognition': buildDesignFilteredBucket(lvl, '2', mapPrinciplesTransactionToFilterItem, 'transactions'),
        'Assumptions, Principles, & Constraints': buildDesignFilteredBucket(lvl, '4', mapPrinciplesTransactionToFilterItem, 'transactions'),
      };
    }

    return {};
  }, [
    assignmentTypeShortened,
    classificationGameScenarioLevels,
    normalGameScenarioLevels,
    beGameScenarioLevels,
    principlesGameScenarioLevels,
    targetEntityType,
    itemEntityFilter,
  ]);

  // Parallel map keyed identically to `filterItems`, but each leaf is
  // the level's design array (as strings). Lets the level-items modal
  // show/hide design-gated filter rows (e.g. design '4' = Classified
  // & Multi-Step). One entry per (decision-type-label → level-name).
  const filterItemsDesign = useMemo(() => {
    const designAsStrings = (design) =>
      Array.isArray(design)
        ? design.map(String)
        : design != null
          ? [String(design)]
          : [];

    const buildLevelsDesignMap = (levels) =>
      (levels || []).reduce((acc, { name, design }) => {
        if (!name) return acc;
        acc[formatScnearioLevelToFilterName(name)] = designAsStrings(design);
        return acc;
      }, {});

    const buildSingleNamedDesignMap = (levels, levelName, label) => {
      const matched = (levels || []).find(l => l.name === levelName);
      return { [label]: designAsStrings(matched?.design) };
    };

    const buildDesignFilteredDesignMap = (levels, designValue) =>
      (levels || [])
        .filter(l => Array.isArray(l.design) && l.design.includes(designValue))
        .reduce((acc, { name, design }) => {
          acc[formatScnearioLevelToFilterName(name)] = designAsStrings(design);
          return acc;
        }, {});

    if (assignmentTypeShortened === 'classification') {
      const lvl = classificationGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Accounts': buildSingleNamedDesignMap(lvl, 'classification-normal-template', 'Accounts'),
        'Deferral & Contra Account': buildSingleNamedDesignMap(lvl, 'deferral-contra-template', 'Deferral & Contra Account'),
        'Classified & Multi-Step': buildDesignFilteredDesignMap(lvl, '4'),
        'Real Companies': buildDesignFilteredDesignMap(lvl, '5'),
      };
    }

    if (assignmentTypeShortened === 'normal') {
      const lvl = normalGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Accounts': buildSingleNamedDesignMap(lvl, 'classification-normal-template', 'Accounts'),
        'Deferral & Contra Account': buildSingleNamedDesignMap(lvl, 'deferral-contra-template', 'Deferral & Contra Account'),
      };
    }

    if (assignmentTypeShortened === 'equation') {
      const lvl = beGameScenarioLevels;
      if (!lvl?.length) return {};
      const allLevels = buildLevelsDesignMap(lvl);
      return {
        'Increase or Decrease': allLevels,
        'Debits & Credits': allLevels,
        'Accounts Impacted': allLevels,
        'Income Statement Impact': allLevels,
        'All Types': allLevels,
      };
    }

    if (assignmentTypeShortened === 'principles') {
      const lvl = principlesGameScenarioLevels;
      if (!lvl?.length) return {};
      return {
        'Revenue & Expense Recognition': buildDesignFilteredDesignMap(lvl, '2'),
        'Assumptions, Principles, & Constraints': buildDesignFilteredDesignMap(lvl, '4'),
      };
    }

    return {};
  }, [
    assignmentTypeShortened,
    classificationGameScenarioLevels,
    normalGameScenarioLevels,
    beGameScenarioLevels,
    principlesGameScenarioLevels,
  ]);

  const handleScenarioLevelAccontSelectSaveButtonClick = async (selectedAccountItems, selectedFilter, selectedFilterParent, accountItemsSavedCallback, assignmentType, selectedDecisionType, equation) => {
    // TEMP debug: see exactly what the modal handed us, including whether
    // each item carries an `explanation` value pulled from the source
    // template. Remove once the multistep flow is verified.
    // eslint-disable-next-line no-console
    console.log('[edit.js] save handler called with', {
      selectedFilter,
      selectedFilterParent,
      selectedDecisionType,
      sample: selectedAccountItems?.[0],
      itemKeys: Object.keys(selectedAccountItems?.[0] || {}),
      explanationsByText: selectedAccountItems?.map(it => ({ text: it.text, explanation: it.explanation })),
    });
    setIsLoading(true);
    let scenarioLevelId, scenarioLevelNumber;
    
    const selectedDesignTypes = designTypesMap[assignmentType]?.[selectedDecisionType] || [];
    const descriptionIndex = assignmentTypeShortened === 'classification' || assignmentTypeShortened === 'normal'
      ? 1
      : scenarioLevels.length > 2
        ? 2
        : scenarioLevels.length;
        
    if(typeof editingScenarioLevelIndex !== 'number') { // creating new scenario level
      let description;
      // Carry the source template's `scoring` payload onto the new level so
      // the assignment inherits the same per-question scoring config the
      // template defines (added field on /scenario-levels/).
      let templateScoring = null;
      let templateNumDecisionsQuestion = null;
      // Resolve the matching template level inside the single source-
      // scenario for this game type (classification-game / normal-game /
      // be-game / principles-game). Whichever level the modal showed
      // becomes the level whose `description` / `scoring` /
      // `num_decisions_question` we copy onto the new level.
      const sourceLevels =
        assignmentType === 'The Principles'   ? principlesGameScenarioLevels :
        assignmentType === 'Balanced Equation' ? beGameScenarioLevels :
        assignmentType === 'Account Classification' ? classificationGameScenarioLevels :
        assignmentType === 'Normal Balance' ? normalGameScenarioLevels :
        [];
      // BE / Principles store entity_type on the level itself, so the
      // lookup needs to disambiguate when same-named levels exist for
      // both entities. Classification / Normal use account_item-level
      // entity_type, so the level lookup stays name-only there.
      const sourceUsesLevelEntityType = (
        assignmentType === 'The Principles' || assignmentType === 'Balanced Equation'
      );
      const filterScenarioLevel = (sourceLevels || []).find(level => {
        const nameMatches = formatScnearioLevelToFilterName(level.name) === formatScnearioLevelToFilterName(selectedFilter);
        if (!nameMatches) return false;
        if (sourceUsesLevelEntityType) {
          return matchesEntityType(level.entity_type, targetEntityType);
        }
        return true;
      });

      if(assignmentType === 'The Principles') {
        description = filterScenarioLevel?.description;
      } else if(assignmentType === 'Balanced Equation') {
        description = 'Decisions';
        switch (selectedDecisionType) {
          case 'increase-decrease':
            description += '/Increase or Decrease';
            break;
          case 'debit-credit2':
            description += '/Debits & Credits';
            break;
          case 'accounts-impacted':
            description += '/Accounts Impacted';
            break;
          case 'income-statement-impact':
            description += '/Income Statement Impact';
            break;
          case 'all-types':
            description += '/All Types';
            break;
          default:
            break;
        }
      } else if(selectedDecisionType === 'real-public-co' || selectedDecisionType === 'classified-multistep') {
        // Real Companies and Classified & Multi-Step inherit the
        // description directly from the selected template so any
        // per-level copy authored on the template carries to the
        // created level. Falls back to the hardcoded text only if
        // the template has no description set.
        description = filterScenarioLevel?.description
          || scenarioLevelsDescription[formData.assignmentType]?.[descriptionIndex];
      } else {
        description = scenarioLevelsDescription[formData.assignmentType][descriptionIndex];
      }
      templateScoring = filterScenarioLevel?.scoring ?? null;
      templateNumDecisionsQuestion = filterScenarioLevel?.num_decisions_question ?? null;

      // Real Companies levels follow the `real-public-co-<DisplayName>`
      // convention (e.g. 'real-public-co-Apple') so the runtime can map
      // back to the company's logo / display name while keeping the
      // created level's name distinct from the source template's
      // (e.g. 'apple-realco'). Every other decision type keeps the
      // decision key as the level's `name` directly — including
      // `debit-credit2`, whose picker key IS the storage name.
      let newLevelName = selectedDecisionType;
      if (selectedDecisionType === 'real-public-co' && filterScenarioLevel?.name) {
        newLevelName = buildRealcoLevelName(filterScenarioLevel.name) || selectedDecisionType;
      }

      const resultCreateScenarioLevels = await createScenarioLevelsMutation.mutateAsync({
        name: newLevelName,
        levelNumber: scenarioLevels.length + 1,
        scenarios: [scenarioId],
        description,
        design: selectedDesignTypes,
        scoring: templateScoring,
        numDecisionsQuestion: templateNumDecisionsQuestion,
        // BE modal pops the equation picker for Increase-or-Decrease
        // creates and passes the picked value ('assets' | 'equity');
        // every other BE card is set to 'assets' automatically; non-BE
        // games pass undefined so the field stays null on the backend.
        equation
      });
      scenarioLevelId = resultCreateScenarioLevels?.id;
      scenarioLevelNumber = resultCreateScenarioLevels.level;
    } else {
      scenarioLevelId = scenarioLevels[editingScenarioLevelIndex]?.id;
      scenarioLevelNumber = scenarioLevels[editingScenarioLevelIndex]?.level;
    }
    
    if(scenarioLevelId || typeof editingScenarioLevelIndex === 'number') { // editing newly created or existing scenario level
      try {
        const remainingPreCheckedItems = [...editingScenarioLevelPrecheckedItems];

        const itemsToProcess = selectedDecisionType === 'assumptions-principles-constraints'
          ? shuffleArray([...selectedAccountItems])
          : selectedAccountItems;

        // Separate new items from existing ones
        const newItems = [];
        for (const [index, gameCreateItemQuestion] of itemsToProcess.entries()) {
          const matchedItemIndex = remainingPreCheckedItems.findIndex(
            (item) => item.text === gameCreateItemQuestion.text
          );

          if (matchedItemIndex === -1) {
            newItems.push({
              order: gameCreateItemQuestion.order || index + 1,
              accountItemId: gameCreateItemQuestion.accountItemId || gameCreateItemQuestion.id,
              explanation: gameCreateItemQuestion.explanation ?? null,
            });
          } else {
            remainingPreCheckedItems.splice(matchedItemIndex, 1);
          }
        }

        // Bulk create new questions
        if (newItems.length) {
          // TEMP debug: confirm explanation is in the request body. Remove
          // once we've verified the multistep save flow is propagating
          // template explanations correctly.
          // eslint-disable-next-line no-console
          console.log('[edit.js] bulk-create payload', {
            selectedDecisionType,
            scenarioLevelId,
            sample: newItems[0],
            count: newItems.length,
            explanationsByOrder: newItems.map(n => ({ order: n.order, explanation: n.explanation })),
          });
          await createItemQuestionsBulkMutation.mutateAsync({
            gameType: assignmentTypeShortened,
            questions: newItems,
            scenarioLevelId
          });
        }

        // Delete removed questions
        if(remainingPreCheckedItems.length) {
          for (const remainingItem of remainingPreCheckedItems) {
            await deleteItemQuestionsMutation.mutateAsync({
              questionId: remainingItem.id,
              gameType: assignmentTypeShortened
            });
          }
        }
      } catch (error) {
        console.log('Some mutations failed:', error);
      } finally {
        // Defense-in-depth: never let a crash in the options-seeding /
        // edit-existing-level paths leak out of the outer function —
        // otherwise the modal never closes and the Save button stays
        // stuck in its loading state (the refetch / callback /
        // setIsLoading cleanup at the end of the handler would be
        // skipped). Root cause of the original lockup is fixed above
        // (classification template-lookup); this wrapper protects
        // against future regressions in the same shape.
        try {
        if(typeof editingScenarioLevelIndex !== 'number') {
          if(assignmentTypeShortened === 'classification') {
            // The 'class-default' (Accounts) and 'class-tricky' (Deferral
            // & Contra Account) decisions build filterItems from a
            // hardcoded bucket label that doesn't correspond to any real
            // scenario-level name, so a round-trip name match against
            // selectedFilter finds nothing and the .options read below
            // throws — locking the modal in its loading state after the
            // level itself was already created. Resolve those two decisions
            // by their well-known template names instead; other classification
            // decisions (classified-multistep, real-public-co) do surface real
            // level names as scenario radios and can continue to use the
            // round-trip match.
            const templateLookupName =
              selectedDecisionType === 'class-default' ? 'classification-normal-template' :
              selectedDecisionType === 'class-tricky'  ? 'deferral-contra-template' :
              selectedFilter.toLowerCase().replace(/\s+/g, '-');
            const filterScenarioLevel = (classificationGameScenarioLevels || []).find(level =>
              level.name.toLowerCase().replace(/\s+/g, '-') === templateLookupName
            );

            // Narrow source-template options to those whose account_item
            // matches the course's business type (corporate/noncorporate).
            const scenarioLevelOptions = (filterScenarioLevel?.options || [])
              .filter(itemEntityFilter);

            // Multi-step (design=4) pulls every option from the source
            // template at orders 3 through 15 — the five financial-statement
            // elements (Assets, Liabilities, Equity, Revenues, Expenses)
            // plus the section drop zones (Current/Non-Current Assets,
            // Current/Non-Current Liabilities, Gross Profit, Operating
            // Profit, Non-Operating, etc.). Revenues / Expenses don't
            // render as drop zones, but their account_item ids ride along
            // so the gameplay component can group items correctly (e.g.
            // computing the "Net Revenues" subtotal in Gross Profit).
            //
            // Real Companies (design=5) uses the same multistep board,
            // so it needs the full template option set too. Pass every
            // option through (entity-filtered above) — the gameplay
            // component picks the sections it needs by name.
            let filterScenarioOptions = [];
            if (selectedDecisionType === 'classified-multistep') {
              filterScenarioOptions = scenarioLevelOptions.filter(option =>
                option.order >= 3 && option.order <= 15
              );
            } else if (selectedDecisionType === 'real-public-co') {
              filterScenarioOptions = scenarioLevelOptions;
            } else {
              switch (scenarioLevelNumber) {
                case 1:
                  filterScenarioOptions = scenarioLevelOptions.filter(option => option.order >= 1 && option.order <= 2);
                  break;
                case 2:
                  filterScenarioOptions = scenarioLevelOptions.filter(option =>
                    (option.order >= 3 && option.order <= 7)
                  );
                  break;
                default:
                  filterScenarioOptions = scenarioLevelOptions.filter(option =>
                    (option.order >= 3 && option.order <= 7)
                  );
                  break;
              }
            }
  
            try {
              const newOptions = filterScenarioOptions.map((opt, index) => ({
                order: opt.order || index + 1,
                accountItemId: opt.account_item.id
              }));
              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: assignmentTypeShortened,
                  options: newOptions,
                  scenarioLevelId
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          } else if (assignmentTypeShortened === 'normal') {
            const gameCreateItemsOptions = gameCreateItemsOptionsNormal;
            const allReferenceOptions = (normalGameScenarioLevels || [])
              .flatMap(lvl => lvl?.normal_options || []);

            try {
              const newOptions = gameCreateItemsOptions
                .map((name, index) => {
                  const matched = allReferenceOptions.find(opt =>
                    opt.account_item?.name === name &&
                    matchesEntityType(opt.account_item?.entity_type, targetEntityType)
                  );
                  return matched ? { order: matched.order || index + 1, accountItemId: matched.account_item.id } : null;
                })
                .filter(Boolean);
              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: assignmentTypeShortened,
                  options: newOptions,
                  scenarioLevelId
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          } else if(assignmentTypeShortened === 'equation') {
            const filterScenarioLevel = (beGameScenarioLevels || []).find(level =>
              level.name.toLowerCase().replace(/\s+/g, '-') === selectedFilter.toLowerCase().replace(/\s+/g, '-') &&
              matchesEntityType(level.entity_type, targetEntityType)
            );
  
            let filterScenarioOptions = [];

            if (selectedDesignTypes.includes("1")) {
              filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => option.order >= 1 && option.order <= 5));
            }

            if (selectedDesignTypes.includes("2")) {
              filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => [1, 2, 3, 6, 7].includes(option.order)));
            }

            if (selectedDesignTypes.includes("3")) {
              filterScenarioOptions.push(
                ...filterScenarioLevel.be_options.filter(option => option.account_item.account === true)
              );
            }

            filterScenarioOptions = [...new Set(filterScenarioOptions)];
  
            try {
              const newOptions = filterScenarioOptions.map((opt, index) => ({
                order: opt.order || index + 1,
                accountItemId: opt.account_item.id
              }));
              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: assignmentTypeShortened,
                  options: newOptions,
                  scenarioLevelId
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          } else if (assignmentTypeShortened === 'principles' && selectedDecisionType === 'assumptions-principles-constraints') {
            const filterScenarioLevel = (principlesGameScenarioLevels || []).find(level =>
              formatScnearioLevelToFilterName(level.name) === formatScnearioLevelToFilterName(selectedFilter) &&
              matchesEntityType(level.entity_type, targetEntityType)
            );

            const sourceOptions = filterScenarioLevel?.principles_options || [];

            try {
              const newOptions = sourceOptions
                .map((opt, index) => ({
                  order: opt.order || index + 1,
                  accountItemId: opt.account_item?.id,
                }))
                .filter((o) => o.accountItemId);
              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: assignmentTypeShortened,
                  options: newOptions,
                  scenarioLevelId
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          }
        } else {
          let description;
          if(assignmentType === 'The Principles') {
            const filterScenarioLevel = (principlesGameScenarioLevels || []).find(level =>
              formatScnearioLevelToFilterName(level.name) === formatScnearioLevelToFilterName(selectedFilter)
            );
            description = filterScenarioLevel?.description;
          } else if(assignmentType === 'Balanced Equation') {
            description = 'Decisions';
            switch (selectedDecisionType) {
              case 'increase-decrease':
                description += '/Increase or Decrease';
                break;
              case 'debit-credit2':
                description += '/Debits & Credits';
                break;
              case 'accounts-impacted':
                description += '/Accounts Impacted';
                break;
              case 'income-statement-impact':
                description += '/Income Statement Impact';
                break;
              case 'all-types':
                description += '/All Types';
                break;
              default:
                break;
            }
          } else {
            description = scenarioLevelsDescription[formData.assignmentType][descriptionIndex];
          }

          await editScenarioLevelsMutation.mutateAsync({
            scenarioLevelId,
            name: selectedDecisionType,
            description,
            design: selectedDesignTypes
          });

          if(assignmentType === 'Balanced Equation') { // update options for BE only
            let designsToAdd = [];
            let designsToRemove = [...editingScenarioLevelPrecheckedDesigns];

            for (const selectedDesign of selectedDesignTypes) {
              const matchedIndex = designsToRemove.indexOf(selectedDesign);

              if (matchedIndex === -1) {
                designsToAdd.push(selectedDesign);
              } else {
                designsToRemove.splice(matchedIndex, 1);
              }
            }

            if (designsToAdd.length) {
              const filterScenarioLevel = (beGameScenarioLevels || []).find(level =>
                level.name.toLowerCase().replace(/\s+/g, '-') === selectedFilter.toLowerCase().replace(/\s+/g, '-') &&
                matchesEntityType(level.entity_type, targetEntityType)
              );
    
              let filterScenarioOptions = [];

              if (designsToAdd.includes('1')) {
                filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => option.order === 4 || option.order === 5));
              }

              if (designsToAdd.includes('2')) {
                filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => option.order === 6 || option.order === 7));
              }

              if (designsToAdd.includes('3')) {
                filterScenarioOptions.push(
                  ...filterScenarioLevel.be_options.filter(option => option.account_item.account === true && ![1, 2, 3].includes(option.order))
                );
              }

              filterScenarioOptions = [...new Set(filterScenarioOptions)];
    
              try {
                const newOptions = filterScenarioOptions.map((opt, index) => ({
                  order: opt.order || index + 1,
                  accountItemId: opt.account_item.id
                }));
                if (newOptions.length) {
                  await createItemOptionsBulkMutation.mutateAsync({
                    gameType: assignmentTypeShortened,
                    options: newOptions,
                    scenarioLevelId
                  });
                }
              } catch (error) {
                console.log('Some mutations failed:', error);
              }
            }
            if (designsToRemove.length) {
              const currentScenarioLevel = scenarioLevels[editingScenarioLevelIndex];
              // await deleteDesignMutation.mutateAsync({ design: remainingDesign });
              let filterScenarioOptions = [];

              if (designsToRemove.includes('1')) {
                filterScenarioOptions.push(
                  ...currentScenarioLevel.be_options.filter(option => option.order === 4 || option.order === 5)
                );
              }

              if (designsToRemove.includes('2')) {
                filterScenarioOptions.push(
                  ...currentScenarioLevel.be_options.filter(option => option.order === 6 || option.order === 7)
                );
              }

              if (designsToRemove.includes('3')) {
                filterScenarioOptions.push(
                  ...currentScenarioLevel.be_options.filter(option => option.account_item.account === true && ![1, 2, 3].includes(option.order))
                );
              }

              filterScenarioOptions = [...new Set(filterScenarioOptions)];

              try {
                for (const [index, deleteItemOption] of filterScenarioOptions.entries()) {
                  await deleteItemOptionsMutation.mutateAsync({optionId: deleteItemOption.id});
                }
              } catch (error) {
                console.log('Some mutations failed:', error);
              }
            }
          }
        }
        } catch (error) {
          console.error('[edit.js] finally-block cleanup failed:', error);
        }
      }
    }

    try {
      await refetchScenarioLevels();
    } catch (error) {
      // Refetch failure shouldn't block modal close / loading clear —
      // the mutations already ran, the UI will hydrate on next render.
      console.error('[edit.js] refetchScenarioLevels failed after save:', error);
    }
    accountItemsSavedCallback();
    setIsLoading(false);
  }

  const handleCloseButtonClick = () => {
    navigate('/instructor/settings/assignments');
  }

  const handleScenarioLevelDelete = async (scenarioLevelId, scenarioLevelDeletedCallback) => {
    deleteScenarioLevelMutation.mutate(
      { scenarioLevelId },
      {
        onSuccess: () => {
          scenarioLevelDeletedCallback();
          refetchScenarioLevels();
        },
      }
    );
  };

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="adjustment-content-container"
      showPagination={false}
      secondButtonName="Close"
      onSecondButtonClick={handleCloseButtonClick}
    >
      <InstructorAssignmentForm
        onSave={handleEditGameSettings}
        isButtonLoading={isLoading}
        initialValues={formData}
      />

      <InstructorAssignmentSetLevels
        gameType={formData.assignmentType}
        scenarioLevels={scenarioLevels}
        onScenarioLevelAccountSelectSave={handleScenarioLevelAccontSelectSaveButtonClick}
        isButtonLoading={isLoading}
        onShowLevelAccounts={handleShowLevelAccountsButtonClick}
        filterItems={filterItems}
        filterItemsDesign={filterItemsDesign}
        editingScenarioLevelIndex={editingScenarioLevelIndex}
        editingScenarioLevelPreCheckedItems={editingScenarioLevelPrecheckedItems}
        onLevelDeletionConfirm={handleScenarioLevelDelete}
        isGameStarted={isGameStarted}
        businessType={courses?.[0]?.businesstype}
      />
    </SubPagesLayout>
  );
}
